import React, { useEffect, useRef } from 'react';
import { Autocomplete, Button, MenuItem, styled, TextField } from '@mui/material';
import Dialog from 'components/dialogs/Dialog';
import { HospitalBank } from 'types/hospital';
import { Bank } from 'types/bank';
import { useHospitalBankValidation } from '../validation/useHospitalBankValidation';
import * as yup from 'yup';

interface HospitalBankFormProps {
  banks: Bank[];
  onExited: () => void;
  handleAddBank: (bankInfo: HospitalBank) => void;
  bankInfo: HospitalBank;
  handleChangeBankInfo<K extends keyof HospitalBank>(index: K, value: HospitalBank[K]): void;
  type: 'new' | 'edit';
}

const Container = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  columnGap: 10,
  rowGap: 15,
});

const DialogActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
});

const HospitalBankForm: React.FC<HospitalBankFormProps> = ({
  handleAddBank,
  onExited,
  banks,
  bankInfo,
  handleChangeBankInfo,
  type,
}) => {
  const [validation, setValidation, validate] = useHospitalBankValidation();
  const inputs = {
    bank_name: useRef<HTMLInputElement>(null),
    account_holder: useRef<HTMLInputElement>(null),
    operation: useRef<HTMLInputElement>(null),
    pix_key: useRef<HTMLInputElement>(null),
    pix_key_type: useRef<HTMLInputElement>(null),
    bank_account: useRef<HTMLInputElement>(null),
    bank_agency: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]);

  function handleValidate(e?: React.FormEvent) {
    e?.preventDefault();
    validate(bankInfo)
      .then(() => {
        handleAddBank(bankInfo);
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  return (
    <Dialog
      onExited={onExited}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      height="auto"
      title={type === 'edit' ? 'Editar conta bancária' : 'Adicionar conta bancária'}
    >
      <Container onSubmit={handleValidate}>
        <Autocomplete
          value={bankInfo.bank_name || ''}
          options={banks.map(bank => bank.name)}
          getOptionLabel={option => option}
          onChange={(event, newValue) => {
            handleChangeBankInfo('bank_name', newValue || '');
          }}
          renderInput={params => (
            <TextField
              {...params}
              error={!!validation.bank_name}
              helperText={validation.bank_name}
              inputRef={inputs.bank_name}
              label="Banco"
              placeholder="Selecione um banco"
              fullWidth
            />
          )}
        />

        <TextField
          error={!!validation.account_holder}
          helperText={validation.account_holder}
          inputRef={inputs.account_holder}
          label="Titular"
          placeholder="Digite o nome do titular"
          value={bankInfo.account_holder || ''}
          onChange={e => handleChangeBankInfo('account_holder', e.target.value)}
          fullWidth
        />

        <TextField
          error={!!validation.bank_agency}
          helperText={validation.bank_agency}
          inputRef={inputs.bank_agency}
          label="Agência"
          placeholder="Digite o número da agência"
          value={bankInfo.bank_agency || ''}
          onChange={e => handleChangeBankInfo('bank_agency', e.target.value)}
          fullWidth
        />

        <TextField
          error={!!validation.bank_account}
          helperText={validation.bank_account}
          inputRef={inputs.bank_account}
          label="Conta"
          placeholder="Digite o número da conta"
          value={bankInfo.bank_account || ''}
          onChange={e => handleChangeBankInfo('bank_account', e.target.value)}
          fullWidth
        />

        <TextField
          select
          error={!!validation.pix_key_type}
          helperText={validation.pix_key_type}
          inputRef={inputs.pix_key_type}
          label="Tipo chave PIX"
          placeholder="Selecione o tipo da chave PIX"
          value={bankInfo.pix_key_type || ''}
          onChange={e => handleChangeBankInfo('pix_key_type', e.target.value)}
          fullWidth
        >
          <MenuItem value="cpf">CPF</MenuItem>
          <MenuItem value="cnpj">CNPJ</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="phone">Telefone</MenuItem>
          <MenuItem value="random">Aleatório</MenuItem>
        </TextField>

        <TextField
          error={!!validation.pix_key}
          helperText={validation.pix_key}
          inputRef={inputs.pix_key}
          label="Chave PIX"
          placeholder="Digite a chave PIX"
          value={bankInfo.pix_key || ''}
          onChange={e => handleChangeBankInfo('pix_key', e.target.value)}
          fullWidth
        />

        <TextField
          error={!!validation.operation}
          helperText={validation.operation}
          inputRef={inputs.operation}
          label="Operação"
          placeholder="Digite o tipo de operação"
          value={bankInfo.operation || ''}
          onChange={e => handleChangeBankInfo('operation', e.target.value)}
          fullWidth
        />

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onExited}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" onClick={handleValidate}>
            {type === 'new' ? 'Adicionar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Container>
    </Dialog>
  );
};

export default HospitalBankForm;
